import { groupBy, sortBy } from 'lodash';
import type { TeamSearchFields, TeamSearchResult } from '../../types';
import { mailingAddressHasValidGeolocation } from '../../utils/location';

export type TeamSearchResultVirtualItem = TeamSearchResult & {
  start: number;
  size: number;
  groupKey: string;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  isFocused: boolean;
};

/**
 * Groups partners that share a location together, and filters out partners
 * that don't have a locationCoordinate.
 *
 *
 * @param partners list of partners with a mailingAddress field, sorted
 * by geolocation coordinates.
 */
export function groupPartnersByLocation(
  partners: TeamSearchFields[],
): Record<string, TeamSearchResult[]> {
  const partnersWithCoords = partners
    .map((partner) => {
      if (!mailingAddressHasValidGeolocation(partner.mailingAddress)) {
        return undefined;
      }
      const result: TeamSearchResult = {
        ...partner,
        mailingAddress: {
          ...partner.mailingAddress,
          geolocationLatitude: Number(
            partner.mailingAddress.geolocationLatitude,
          ),
          geolocationLongitude: Number(
            partner.mailingAddress.geolocationLongitude,
          ),
        },
      };
      return result;
    })
    .filter((partner): partner is TeamSearchResult => !!partner);

  return Object.fromEntries(
    Object.entries(
      groupBy(
        partnersWithCoords,
        ({ mailingAddress }) =>
          `${mailingAddress.street1}_${mailingAddress.city}_${mailingAddress.state}`,
      ),
    ).map(([key, groupPartners]) => [
      key,
      sortBy(groupPartners, (partner) =>
        [partner.legalName, partner.alias].filter((v) => !!v).join(' - '),
      ),
    ]),
  );
}
